import { Splashscreen } from '@/components/splashscreen/splashscreen';
import { readPhoneQueryParam } from '@/lib/allo-call-url';
import { isDesktop } from '@/lib/platform';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';

/**
 * Deep-link route that triggers a call for a given phone number.
 *
 * Uses a path parameter rather than a search parameter because `+` decodes
 * to a space in `URLSearchParams`, which mangles international phone numbers
 * and confuses TanStack's default search canonicaliser. Path segments
 * preserve `+` literally end-to-end, which lets a CRM author the natural
 * `+33...` form without URL-encoding tricks.
 *
 * An optional `from` query param pins which of the user's lines places the
 * call (for users with several numbers). It is read straight from
 * `window.location.search` for the same reason `number` is a path param: the
 * search canonicaliser would mangle its `+`. It is forwarded into the
 * `allo://call` hand-off, where the desktop main process resolves it.
 *
 * - Desktop: redirects to the inbox; calls launched via `allo://call?...`
 *   are intercepted by the main process and never reach this route.
 * - Web: launches the desktop app via the `allo://` custom protocol so an
 *   installed desktop client picks up the call; falls back to the inbox.
 *
 * Customer-facing URLs:
 * - `https://web.withallo.com/call/+33612345678` (works in any browser)
 * - `https://web.withallo.com/call/+33612345678?from=+33987654321` (pin a line)
 * - `allo://call?number=+33612345678&from=+33987654321` (custom-protocol form)
 */
export const Route = createFileRoute('/_app/call/$number')({
  component: RouteComponent,
});

function RouteComponent() {
  const { number } = Route.useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (number && !isDesktop()) {
      const from = readPhoneQueryParam(window.location.search, 'from');
      const query = new URLSearchParams({ number });
      if (from) {
        query.set('from', from);
      }
      window.location.href = `allo://call?${query.toString()}`;
    }

    const timeout = setTimeout(() => {
      void navigate({ to: '/', replace: true });
    }, 1500);

    return () => clearTimeout(timeout);
  }, [number, navigate]);

  return <Splashscreen />;
}
